import { Howler } from 'howler'
import { t, onLang } from '../content/i18n.js'
import { isMobileLayout, onMobileLayoutChange } from './Utils/layout.js'

/**
 * Masaüstü: sağ altta (Menü düğmesiyle aynı hizada) Müzik ve Ses (çarpma / efekt) anahtarları
 * (Sounds.js musicEnabled / sfxEnabled; tercih saklanır). Dokunmatik: üst çubukta tek "sesi aç / kapat" simgesi.
 * Menüdeki gibi olaylar oyuna geçmez; tıklamadan sonra odak bırakılır (Space/Enter oyunda kalsın).
 */
const ISOLATED_EVENTS = ['keydown', 'keyup', 'mousedown', 'mouseup', 'mousemove', 'click', 'touchstart', 'touchmove', 'touchend', 'wheel', 'mousewheel']

const ICONS = {
    speaker: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5h3.2L12 5.2v13.6l-4.8-4.3H4z" fill="currentColor"/><path d="M15.2 8.6a4.6 4.6 0 0 1 0 6.8M17.6 6.2a8 8 0 0 1 0 11.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    speakerOff: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5h3.2L12 5.2v13.6l-4.8-4.3H4z" fill="currentColor"/><path d="M15.5 9.5l5 5M20.5 9.5l-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    music: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 17.5a3 3 0 1 1-2-2.83V5.2l11-2.2v11.5a3 3 0 1 1-2-2.83V6.3l-7 1.4v9.8z" fill="currentColor"/></svg>',
    sfx: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2l1.9 5.2 5.3-2.1-2.3 5.1 5.1 2.2-5.3 1.9 2.1 5.3-5.1-2.3L11.5 22l-1.9-5.2-5.3 2.1 2.3-5.1L1.5 11.6l5.3-1.9L4.7 4.4l5.1 2.3z" fill="currentColor"/></svg>'
}

export default class SoundToggles
{
    constructor(_options)
    {
        this.sounds = _options.world.sounds

        this.element = document.createElement('div')
        this.element.className = 'sound-toggles'
        document.body.append(this.element)

        for(const _type of ISOLATED_EVENTS)
        {
            this.element.addEventListener(_type, (_event) => _event.stopPropagation(), { passive: _type.startsWith('touch') || _type.endsWith('wheel') })
        }

        this.config = _options.config

        this.render()
        this.sounds.toggleListeners.add(() => this.render())
        this.sounds.muteListeners.add(() => this.render())
        onMobileLayoutChange(() => this.render())
        onLang(() => this.render())

        // Tarayıcı, kullanıcı dokunmadan ses çaldırmaz (START yok): ilk tuş / tıklamada Howler sesi açar,
        // o zamana kadar "ses için tıkla" ipucu görünür
        if(Howler.ctx)
        {
            Howler.ctx.addEventListener('statechange', () => this.render())
        }
    }

    render()
    {
        // Dokunmatik: tek yuvarlak "sesi aç / kapat" simgesi (Menü'nün solunda); müzik / efekt ayrıntısı Menü → Ortam'da.
        // Tarayıcı ses kilidi açık değilse simge kapalı görünür; dokununca kilit açılır ve ses gelir.
        if(isMobileLayout())
        {
            const locked = Howler.ctx && Howler.ctx.state !== 'running'
            const on = !locked && !this.sounds.muted
            this.element.innerHTML = `<button type="button" class="sound-master${on ? ' is-on' : ''}" aria-pressed="${on}" aria-label="${on ? t('muteSound') : t('unmuteSound')}">${on ? ICONS.speaker : ICONS.speakerOff}</button>`
            const button = this.element.querySelector('.sound-master')
            button.addEventListener('click', () =>
            {
                if(locked)
                {
                    Howler.ctx.resume()
                    this.sounds.setMuted(false)
                }
                else
                {
                    this.sounds.setMuted(!this.sounds.muted)
                }
                button.blur()
            })
            return
        }

        const items = [
            ['music', t('musicToggle'), this.sounds.musicEnabled],
            ['sfx', t('sfxToggle'), this.sounds.sfxEnabled]
        ]
        const locked = Howler.ctx && Howler.ctx.state !== 'running'
        const touch = (this.config && this.config.touch) || window.matchMedia('(pointer: coarse)').matches
        const hint = locked ? `<button type="button" class="sound-hint" data-hint>${t(touch ? 'soundHintTouch' : 'soundHint')}</button>` : ''
        this.element.innerHTML = hint + items.map(([_id, _label, _on]) => `
            <button type="button" class="sound-toggle${_on ? ' is-on' : ''}" data-toggle="${_id}" aria-pressed="${_on}" aria-label="${_label}: ${_on ? t('soundOn') : t('soundOff')}" title="${_label}">
                <span class="sound-toggle__icon">${ICONS[_id]}</span>
                <span class="sound-toggle__label">${_label}</span>
            </button>`).join('')

        const hintButton = this.element.querySelector('[data-hint]')
        if(hintButton)
        {
            hintButton.addEventListener('click', () =>
            {
                Howler.ctx.resume()
                hintButton.blur()
            })
        }

        for(const _button of this.element.querySelectorAll('[data-toggle]'))
        {
            _button.addEventListener('click', () =>
            {
                if(_button.dataset.toggle === 'music')
                {
                    this.sounds.setMusicEnabled(!this.sounds.musicEnabled)
                }
                else
                {
                    this.sounds.setSfxEnabled(!this.sounds.sfxEnabled)
                }
                _button.blur()
            })
        }
    }
}
